import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useBookAudioPlayer } from '../../context/bookAudioPlayerContext';
import AudioPauseIcon from '../../images/svg-gcbooks/audio-pause-icon';
import AudioPlayIcon from '../../images/svg-gcbooks/audio-play-icon';
import ProgressBar from '../../components/ProgressBar';
import UKflag from '../../images/svg-gcbooks/ukflag.png';
import USflag from '../../images/svg-gcbooks/usflag.png';
import AUSflag from '../../images/svg-gcbooks/ausflag.jpg';
import SpeedSelector from './playbackMenu';
import useBreakpoint from '../../hooks/useBreakpoint';

export default function MobileBookAudioPlayer() {
  const {
    toggle,
    progressPercent,
    currentTime,
    audioState,
    totalDurations,
    handleNext,
    handlePrevious,
    rawTotalDuration,
    handleSeek,
    handleClose,
    currentTimeInSeconds,
    audioDetails,
    selectedVoice,
    setSelectedVoice,
    playbackSpeed,
  } = useBookAudioPlayer();
  const isPlaying = audioState === 'playing';

  const voices = [
    {
      category: 'Modern',
      name: 'Charlene',
      flag: UKflag,
    },
    {
      category: 'Original',
      name: 'Callie',
      flag: USflag,
    },
    {
      category: 'Dramatized',
      name: 'Nancy',
      flag: AUSflag,
    },
  ];

  const handleSelectVoice = (voice) => {
    setSelectedVoice(voice);
    setIsVoiceDropdownOpen(false);
  };

  const currentVoice = voices.find((v) => v.name === selectedVoice) || voices[0];
  const [showVoiceSettings, setShowVoiceSettings] = useState(false);
  const [showPlaybackMenu, setShowPlaybackMenu] = useState(false);
  const [isVoiceDropdownOpen, setIsVoiceDropdownOpen] = useState(false);
  const containerRef = useRef(null);
  const breakpoint = useBreakpoint();

  // Portal components
  const VoiceSettingsPortal = ({ children }) => {
    return ReactDOM.createPortal(children, document.body);
  };

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowVoiceSettings(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setShowVoiceSettings]);

  return (
    <div className="mobile-book-audio-player">
      {breakpoint.mobile && (
        <ProgressBar
          showThumb
          trackStyle={{
            height: '4px',
          }}
          min={0}
          max={rawTotalDuration}
          value={currentTimeInSeconds}
          onChange={handleSeek}
          style={{ zIndex: 1000 }}
        />
      )}
      <div className="audio-player-container">
        <div className="audio-player-control-buttons-container">
          <div className="audio-controls">
            {/* Previous Button */}
            <button className="control-btn previous-btn" onClick={handlePrevious} aria-label="Previous">
              <svg width="11" height="16" viewBox="0 0 11 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10.75 0V15.328L3.086 7.664L10.75 0ZM2 0.664V14.664H0V0.664H2Z" fill="#F6F6F7" />
              </svg>
            </button>
            {/* Play/Pause Button */}
            <button className="control-btn play-pause-btn" onClick={() => toggle(audioDetails)}>
              {isPlaying ? <AudioPauseIcon /> : <AudioPlayIcon />}
            </button>
            {/* Next Button */}
            <button className="control-btn next-btn" onClick={handleNext} aria-label="Next">
              <svg width="11" height="16" viewBox="0 0 11 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 0V15.328L7.664 7.664L0 0ZM8.75 0.664V14.664H10.75V0.664H8.75Z" fill="#F6F6F7" />
              </svg>
            </button>
          </div>
          {/* Track Information */}
          <div className="track-info">
            <div className="track-title">The Great Controversy - Chapter {audioDetails?.index + 1}</div>
            <div className="track-subtitle">{audioDetails.audioTitle}</div>
          </div>
        </div>
        <div className="progress-container">
          {/* Current Time */}
          <div className="time-current">{currentTime}</div>

          {/* Progress Bar */}
          <ProgressBar
            showThumb
            trackStyle={{
              height: '4px',
            }}
            min={0}
            max={rawTotalDuration}
            value={currentTimeInSeconds}
            onChange={handleSeek}
          />

          {/* Total Duration */}
          <div className="time-total">{totalDurations}</div>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <div onClick={() => setShowVoiceSettings(!showVoiceSettings)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M4 21V14M4 10V3M12 21V12M12 8V3M20 21V16M20 12V3M1 14H7M9 8H15M17 16H23"
                stroke="#FECB04"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <button className="close-btn" onClick={handleClose}>
            x
          </button>
        </div>

        {/* Voice Settings Portal */}
        {showVoiceSettings && (
          <VoiceSettingsPortal>
            <div className="voice-settings-container-portal" ref={containerRef}>
              <div
                className="voice-settings-option"
                onClick={() => {
                  setShowPlaybackMenu(true);
                  setShowVoiceSettings(false);
                }}
              >
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M10.45 15.5C10.85 15.9 11.3667 16.096 12 16.088C12.6333 16.08 13.1 15.8507 13.4 15.4L17.625 9.075C17.775 8.84167 17.7543 8.629 17.563 8.437C17.3717 8.245 17.159 8.22433 16.925 8.375L10.6 12.6C10.15 12.9 9.91267 13.3583 9.888 13.975C9.86333 14.5917 10.0507 15.1 10.45 15.5ZM12 4C12.6 4 13.1917 4.05 13.775 4.15C14.3583 4.25 14.925 4.40833 15.475 4.625C15.7417 4.725 16.025 4.91267 16.325 5.188C16.625 5.46333 16.7083 5.72567 16.575 5.975C16.4417 6.22433 16.1417 6.391 15.675 6.475C15.2083 6.559 14.8333 6.55067 14.55 6.45C14.1333 6.3 13.7127 6.18767 13.288 6.113C12.8633 6.03833 12.434 6.00067 12 6C9.78333 6 7.89567 6.77933 6.337 8.338C4.77833 9.89667 3.99933 11.784 4 14C4 14.7 4.096 15.3917 4.288 16.075C4.48 16.7583 4.75067 17.4 5.1 18H18.9C19.2833 17.3667 19.5627 16.7083 19.738 16.025C19.9133 15.3417 20.0007 14.6333 20 13.9C20 13.4667 19.9623 13.0417 19.887 12.625C19.8117 12.2083 19.6993 11.8 19.55 11.4C19.45 11.1167 19.4333 10.8417 19.5 10.575C19.5667 10.3083 19.7167 10.0833 19.95 9.9C20.1667 9.73333 20.4043 9.68333 20.663 9.75C20.9217 9.81667 21.1007 9.96667 21.2 10.2C21.45 10.7833 21.6417 11.3793 21.775 11.988C21.9083 12.5967 21.9833 13.2173 22 13.85C22.0167 14.8 21.9083 15.7083 21.675 16.575C21.4417 17.4417 21.1 18.2667 20.65 19.05C20.4667 19.35 20.2167 19.5833 19.9 19.75C19.5833 19.9167 19.25 20 18.9 20H5.1C4.75 20 4.41667 19.9167 4.1 19.75C3.78333 19.5833 3.53333 19.35 3.35 19.05C2.91667 18.3 2.58333 17.5043 2.35 16.663C2.11667 15.8217 2 14.934 2 14C2 12.6167 2.26267 11.321 2.788 10.113C3.31333 8.905 4.03 7.84667 4.938 6.938C5.846 6.02933 6.90833 5.31267 8.125 4.788C9.34167 4.26333 10.6333 4.00067 12 4Z"
                      fill="#FECB04"
                    />
                  </svg>
                  Playback Rate
                </div>
                <span style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  {playbackSpeed}x
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M3.87109 10.2583L8.12988 5.99951L3.87109 1.74072"
                      stroke="#FECB04"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </div>
              <div
                className="voice-settings-option"
                onClick={() => {
                  setIsVoiceDropdownOpen(true);
                  setShowVoiceSettings(false);
                }}
              >
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#clip0_18663_9277)">
                      <path
                        d="M12 22C17.523 22 22 17.523 22 12C22 6.477 17.523 2 12 2C6.477 2 2 6.477 2 12C2 17.523 6.477 22 12 22Z"
                        stroke="#DEB103"
                        strokeWidth="2"
                      />
                      <path
                        d="M15 9V15M18 11V13M9 9V15M6 11V13M12 7V17"
                        stroke="#DEB103"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_18663_9277">
                        <rect width="24" height="24" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  Voice
                </div>
                <span style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <img src={currentVoice.flag} alt={currentVoice.name} className="voice-flag" />
                  {selectedVoice || 'Charlene'}
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M3.87109 10.2583L8.12988 5.99951L3.87109 1.74072"
                      stroke="#FECB04"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </div>
            </div>
          </VoiceSettingsPortal>
        )}

        {/* Playback Menu Portal */}
        {showPlaybackMenu && (
          <VoiceSettingsPortal>
            <div className="speed-selector-portal">
              <SpeedSelector setShowPlaybackMenu={setShowPlaybackMenu} setShowVoiceSettings={setShowVoiceSettings} />
            </div>
          </VoiceSettingsPortal>
        )}

        {/* Voice Dropdown Portal */}
        {isVoiceDropdownOpen && (
          <VoiceSettingsPortal>
            <div className="voice-dropdown-menu-portal" onMouseLeave={() => setIsVoiceDropdownOpen(false)}>
              <div
                className="back-button"
                onClick={() => {
                  setIsVoiceDropdownOpen(false);
                  setShowVoiceSettings(true);
                }}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M8.12891 10.2583L3.87012 5.99951L8.12891 1.74072"
                    stroke="#FECB04"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Voice
              </div>
              {voices.map((voice, index) => (
                <div key={voice.name}>
                  {(index === 0 || voices[index - 1].category !== voice.category) && (
                    <div className="voice-category-label">{voice.category}</div>
                  )}
                  <button
                    className={`voice-option-item ${selectedVoice === voice.name ? 'active' : ''}`}
                    onClick={() => handleSelectVoice(voice.name)}
                  >
                    <img src={voice.flag} alt={voice.name} className="voice-option-flag" />
                    <span className="voice-option-name">{voice.name}</span>
                    <div className={`radio-circle ${selectedVoice === voice.name ? 'selected' : ''}`}>
                      {selectedVoice === voice.name && <div className="radio-inner" />}
                    </div>
                  </button>
                </div>
              ))}
            </div>
          </VoiceSettingsPortal>
        )}
      </div>
    </div>
  );
}
